<script setup lang="ts">
import dayjs from 'dayjs'
import { Activity, ArrowRight, Check, ChevronRight, Power, Search, X } from 'lucide-vue-next'
import { useDebounceFn } from '@vueuse/core'

import type { OrderTabletFlow, Tablet, TabletDeviceEvent, TabletEventRange, TabletLifecycleEvent } from '@/types/types'
import {
  computeMedian,
  groupItemsByDay,
  dayRelativeLabel,
  summarizeTabletEventMetadata,
  formatTabletEventChangeValue,
  formatLatency,
  formatOfflineDuration,
  readTabletOfflineMetadata,
  mergeConsecutiveByKey,
  getDisconnectSeverity,
  getDisconnectReasonKey,
  connectionHintForUnackedOrder,
  resolveDeviceLinkSummary,
  type DisconnectSeverity,
  type UnackedConnectionHint
} from '@/utils/tabletDiagnosticHelpers'
import { getApiErrorMessage } from '@/utils/apiError.js'

const ANOMALY_THRESHOLD_MS = 3000
const POLL_INTERVAL_MS = 15000

type FlowStepKind = 'closed' | 'closedFromWebhook' | 'webhook' | 'sent' | 'ack' | 'checkin'

type FlowStep = {
  kind: FlowStepKind
  label: string
  time: string | null
  isPending: boolean
  meta: string | null
}

type FlowSegmentKind = 'webhook' | 'dispatch' | 'ack' | 'checkin'

type FlowSegment = {
  formatted: string | null
  isAnomalous: boolean
  kind: FlowSegmentKind
}

type FlowRow = {
  orderId: number
  orderNumber: string | null
  rawStart: string
  steps: FlowStep[]
  segments: FlowSegment[]
  totalMs: number | null
  isLatencyAnomalous: boolean
  isPending: boolean
  warning: string | null
  unackedWarning: string | null
}

type TimelineItem =
  | { kind: 'order'; key: string; sortAt: string; row: FlowRow }
  | { kind: 'lifecycle'; key: string; sortAt: string; events: TabletLifecycleEvent[] }
  | { kind: 'device'; key: string; sortAt: string; events: TabletDeviceEvent[] }

type FilterKey = 'all' | 'anomaly' | 'unacked' | 'conn' | 'device'

type DayGroup = {
  key: string
  title: string
  subtitle: string | null
  items: TimelineItem[]
}

const props = defineProps<{ tablet: Tablet; isOpen: boolean }>()
const emit = defineEmits<{ (e: 'update:isOpen', value: boolean): void }>()

const { t } = useI18n()
const { getTabletOrdersFlow, getTabletLifecycleEvents, getTabletDeviceEvents } = useTablet()
const notification = useNotification()

const isLoading = ref(false)
const flow = ref<OrderTabletFlow[]>([])
const lifecycleEvents = ref<TabletLifecycleEvent[]>([])
const deviceEvents = ref<TabletDeviceEvent[]>([])
const openState = ref<Record<string, boolean>>({})
const searchQuery = ref('')
const hasSearched = computed(() => searchQuery.value.trim() !== '')
const activeFilter = ref<FilterKey>('all')
// 'default' maps to 7j for every feed so orders, connectivity and device events share one window.
type RangeSelection = 'default' | TabletEventRange
const eventRange = ref<RangeSelection>('default')

const apiEventRange = computed((): TabletEventRange => (eventRange.value === 'default' ? '7j' : eventRange.value))
const isLive = ref(false)
const lastRefreshedAt = ref(Date.now())
const tick = ref(0)

let pollTimer: ReturnType<typeof setInterval> | null = null
let tickTimer: ReturnType<typeof setInterval> | null = null

const formatTime = (date: string): string => dayjs(date).format('HH:mm:ss')

const toSegment = (ms: number | null, kind: FlowSegmentKind): FlowSegment =>
  ms === null
    ? { formatted: null, isAnomalous: false, kind }
    : {
        formatted: formatLatency(ms),
        // Checkin is a human-paced action (customer scanning at the counter), not a technical
        // step: flagging it past the same 3s threshold as the automated steps would just be noise.
        isAnomalous: kind !== 'checkin' && ms > ANOMALY_THRESHOLD_MS,
        kind
      }

const toFlowRow = (row: OrderTabletFlow): FlowRow => {
  const steps: FlowStep[] = []
  const segments: FlowSegment[] = []

  if (row.orderedAt) {
    steps.push({
      kind: 'closed',
      label: t('pages.marketplace.tabletsPage.orderFlow.steps.closed'),
      time: formatTime(row.orderedAt),
      isPending: false,
      meta: null
    })
    segments.push(toSegment(row.webhookDelayMs, 'webhook'))

    steps.push({
      kind: 'webhook',
      label: t('pages.marketplace.tabletsPage.orderFlow.steps.webhook'),
      time: formatTime(row.receivedByWebhookAt),
      isPending: false,
      meta: null
    })
  } else {
    // Partner didn't send a closing date: the webhook receipt is our earliest signal,
    // shown as the closing step but labeled so it's clear it isn't the partner's own timestamp.
    steps.push({
      kind: 'closedFromWebhook',
      label: t('pages.marketplace.tabletsPage.orderFlow.steps.closedFromWebhook'),
      time: formatTime(row.receivedByWebhookAt),
      isPending: false,
      meta: null
    })
  }

  segments.push(toSegment(dayjs(row.sentToTabletAt).diff(row.receivedByWebhookAt), 'dispatch'))

  steps.push({
    kind: 'sent',
    label: t('pages.marketplace.tabletsPage.orderFlow.steps.sent'),
    time: formatTime(row.sentToTabletAt),
    isPending: false,
    meta: null
  })
  segments.push(toSegment(row.ackLatencyMs, 'ack'))

  steps.push({
    kind: 'ack',
    label: t('pages.marketplace.tabletsPage.orderFlow.steps.ack'),
    time: row.receivedByApiAt ? formatTime(row.receivedByApiAt) : null,
    isPending: row.receivedByApiAt === null,
    meta: null
  })

  // Most orders never get a loyalty scan, so only add the step when one actually happened,
  // rather than showing a permanently-pending row for something that's opt-in for the customer.
  if (row.checkinAt) {
    segments.push(toSegment(row.checkinDelayMs, 'checkin'))

    const pointsMeta =
      row.checkinPoints !== null
        ? t('pages.marketplace.tabletsPage.orderFlow.checkinPointsMeta', { points: row.checkinPoints })
        : null
    const invalidMeta =
      row.checkinIsValid === false ? t('pages.marketplace.tabletsPage.orderFlow.checkinInvalid') : null

    steps.push({
      kind: 'checkin',
      label: t('pages.marketplace.tabletsPage.orderFlow.steps.checkin'),
      time: formatTime(row.checkinAt),
      isPending: false,
      meta: [pointsMeta, invalidMeta].filter(Boolean).join(' · ') || null
    })
  }

  const start = row.orderedAt ?? row.receivedByWebhookAt
  const isPending = row.receivedByApiAt === null
  const totalMs = row.receivedByApiAt ? dayjs(row.receivedByApiAt).diff(start) : null

  return {
    orderId: row.orderId,
    orderNumber: row.orderNumber,
    rawStart: start,
    steps,
    segments,
    totalMs,
    isPending,
    warning: toAnomalyWarning(segments),
    unackedWarning: null,
    isLatencyAnomalous: segments.some((segment) => segment.isAnomalous)
  }
}

const toUnackedWarning = (isPending: boolean, hint: UnackedConnectionHint | null): string | null => {
  if (!isPending) {
    return null
  }

  if (hint) {
    return t(`pages.marketplace.tabletsPage.orderFlow.unackedWarnings.withHint.${hint}`)
  }

  return t('pages.marketplace.tabletsPage.orderFlow.unackedWarnings.generic')
}

const rows = computed<FlowRow[]>(() =>
  flow.value.map((row) => {
    const base = toFlowRow(row)
    const hint = base.isPending ? connectionHintForUnackedOrder(row.sentToTabletAt, lifecycleEvents.value) : null

    return {
      ...base,
      unackedWarning: toUnackedWarning(base.isPending, hint)
    }
  })
)

const timelineItems = computed<TimelineItem[]>(() => {
  const orderItems: TimelineItem[] = rows.value.map((row) => ({
    kind: 'order',
    key: `order-${row.orderId}`,
    sortAt: row.rawStart,
    row
  }))

  const sortedLifecycle = [...lifecycleEvents.value].sort((a, b) => dayjs(b.timestamp).diff(dayjs(a.timestamp)))
  // Grouped by type + reason, not exact timestamp: several disconnects in a row sharing the
  // same code are the "it happened repeatedly" signal, not the exact millisecond of each one.
  // A 'connected' event of a different key in between naturally ends the run. 'flapping' rows
  // are already an hourly backend summary (backend#9444) — keyed by timestamp so two distinct
  // hourly bursts never get collapsed into one another.
  const lifecycleItems: TimelineItem[] = mergeConsecutiveByKey(sortedLifecycle, (event) =>
    event.eventType === 'flapping'
      ? `flapping|${event.timestamp}`
      : `${event.eventType}|${event.disconnectReason ?? ''}`
  ).map((events) => ({
    kind: 'lifecycle',
    key: `lifecycle-${events[0].timestamp}-${events[0].eventType}-${events[0].disconnectReason ?? ''}`,
    sortAt: events[0].timestamp,
    events
  }))

  const sortedDevice = [...deviceEvents.value].sort((a, b) => dayjs(b.timestamp).diff(dayjs(a.timestamp)))
  const deviceItems: TimelineItem[] = mergeConsecutiveByKey(
    sortedDevice,
    (event) =>
      `${event.eventType}|${event.timestamp}|${event.source}|${event.deviceId ?? ''}|${event.previousDeviceId ?? ''}|${event.actorName ?? ''}`
  ).map((events) => ({
    kind: 'device',
    key: `device-${events[0].timestamp}-${events[0].eventType}-${events[0].source}-${events[0].deviceId ?? ''}`,
    sortAt: events[0].timestamp,
    events
  }))

  return [...orderItems, ...lifecycleItems, ...deviceItems].sort((a, b) => dayjs(b.sortAt).diff(dayjs(a.sortAt)))
})

const filterCounts = computed(() => ({
  all: timelineItems.value.length,
  anomaly: timelineItems.value.filter((item) => item.kind === 'order' && item.row.isLatencyAnomalous).length,
  unacked: timelineItems.value.filter((item) => item.kind === 'order' && item.row.isPending).length,
  conn: timelineItems.value.filter((item) => item.kind === 'lifecycle').length,
  device: timelineItems.value.filter((item) => item.kind === 'device').length
}))

const filterChips = computed(() => [
  {
    key: 'all' as const,
    label: t('pages.marketplace.tabletsPage.orderFlow.filters.all'),
    count: filterCounts.value.all
  },
  {
    key: 'anomaly' as const,
    label: t('pages.marketplace.tabletsPage.orderFlow.filters.anomaly'),
    count: filterCounts.value.anomaly
  },
  {
    key: 'unacked' as const,
    label: t('pages.marketplace.tabletsPage.orderFlow.filters.unacked'),
    count: filterCounts.value.unacked
  },
  {
    key: 'conn' as const,
    label: t('pages.marketplace.tabletsPage.orderFlow.filters.connection'),
    count: filterCounts.value.conn
  },
  {
    key: 'device' as const,
    label: t('pages.marketplace.tabletsPage.orderFlow.filters.device'),
    count: filterCounts.value.device
  }
])

const rangeOptions = computed<{ value: RangeSelection; label: string }[]>(() => [
  { value: 'default', label: t('pages.marketplace.tabletsPage.orderFlow.range.default') },
  { value: '24h', label: t('pages.marketplace.tabletsPage.orderFlow.range.24h') },
  { value: '7j', label: t('pages.marketplace.tabletsPage.orderFlow.range.7j') },
  { value: '30j', label: t('pages.marketplace.tabletsPage.orderFlow.range.30j') }
])

const filteredTimelineItems = computed<TimelineItem[]>(() => {
  switch (activeFilter.value) {
    case 'all':
      return timelineItems.value
    case 'conn':
      return timelineItems.value.filter((item) => item.kind === 'lifecycle')
    case 'device':
      return timelineItems.value.filter((item) => item.kind === 'device')
    case 'anomaly':
      return timelineItems.value.filter((item) => item.kind === 'order' && item.row.isLatencyAnomalous)
    case 'unacked':
      return timelineItems.value.filter((item) => item.kind === 'order' && item.row.isPending)
    default:
      return timelineItems.value
  }
})

const collapsedDayKeys = ref<Set<string>>(new Set())

const syncDayGroupCollapse = (): void => {
  const dayKeys = groupItemsByDay(filteredTimelineItems.value, (item) => item.sortAt)
    .map(({ key }) => key)
    .sort((a, b) => dayjs(b).diff(dayjs(a)))

  if (dayKeys.length <= 1) {
    collapsedDayKeys.value = new Set()
    return
  }

  collapsedDayKeys.value = new Set(dayKeys.slice(1))
}

const collapsedDayCount = computed(() => collapsedDayKeys.value.size)

const isDayGroupExpanded = (dayKey: string): boolean => !collapsedDayKeys.value.has(dayKey)

const toggleDayGroup = (dayKey: string): void => {
  const next = new Set(collapsedDayKeys.value)
  if (next.has(dayKey)) {
    next.delete(dayKey)
  } else {
    next.add(dayKey)
  }
  collapsedDayKeys.value = next
}

const expandAllDayGroups = (): void => {
  collapsedDayKeys.value = new Set()
}

const activeRangeLabel = computed(
  () => rangeOptions.value.find((option) => option.value === apiEventRange.value)?.label ?? ''
)

const dayLabels: Record<'today' | 'yesterday', string> = {
  today: t('pages.marketplace.tabletsPage.orderFlow.dayToday'),
  yesterday: t('pages.marketplace.tabletsPage.orderFlow.dayYesterday')
}

const groupedByDay = computed<DayGroup[]>(() =>
  groupItemsByDay(filteredTimelineItems.value, (item) => item.sortAt)
    .map(({ key, items }) => {
      const relative = dayRelativeLabel(key)

      // Numeric date, not written day/month names: dayjs.locale() is hardcoded to 'fr' app-wide
      // (App.vue) regardless of the dashboard's active language, so spelled-out names would show
      // French words to EN/IT/ES users. The relative subtitle already carries the human-friendly
      // cue, and it goes through real i18n.
      return { key, title: dayjs(key).format('L'), subtitle: relative ? dayLabels[relative] : null, items }
    })
    .sort((a, b) => dayjs(b.key).diff(dayjs(a.key)))
)

const lastLifecycleEvent = computed<TabletLifecycleEvent | null>(() => {
  if (!lifecycleEvents.value.length) return null
  return [...lifecycleEvents.value].sort((a, b) => dayjs(b.timestamp).diff(dayjs(a.timestamp)))[0]
})

const medianLatencyMs = computed<number | null>(() =>
  computeMedian(rows.value.map((row) => row.totalMs).filter((ms): ms is number => ms !== null))
)

const latencyAnomalyCount = computed(() => rows.value.filter((row) => row.isLatencyAnomalous).length)
const unackedCount = computed(() => rows.value.filter((row) => row.isPending).length)
const showUnackedHealthBanner = computed(() => unackedCount.value > 0)
// Only "watch" and "concerning" disconnects count as a real outage — a benign one (app closed
// on purpose, instant reconnect with the same id...) isn't a cut worth alerting on. A 'flapping'
// row is a throttled summary standing in for `flapCount` individual disconnects the backend
// didn't write to avoid flooding MySQL (backend#9444) — each one still counts toward the total.
const significantDisconnectionCount = computed(() =>
  lifecycleEvents.value.reduce((total, event) => {
    if (event.eventType === 'disconnected' && getDisconnectSeverity(event.disconnectReason) !== 'benign') {
      return total + 1
    }
    if (event.eventType === 'flapping') return total + (event.flapCount ?? 1)
    return total
  }, 0)
)
const lastDisconnectEvent = computed<TabletLifecycleEvent | null>(() => {
  const candidates = lifecycleEvents.value.filter(
    (event) =>
      (event.eventType === 'disconnected' && getDisconnectSeverity(event.disconnectReason) !== 'benign') ||
      event.eventType === 'flapping'
  )
  if (!candidates.length) return null
  return [...candidates].sort((a, b) => dayjs(b.timestamp).diff(dayjs(a.timestamp)))[0]
})
const flappingHoursCount = computed(
  () => lifecycleEvents.value.filter((event) => event.eventType === 'flapping').length
)

// The current state should read "unstable" for the whole hour a flapping summary covers, not
// flip back to "Connected" just because a stray connect ping landed after it within that same
// hour — a single connect in the middle of a flapping burst doesn't mean the tablet settled down.
const effectiveCurrentEvent = computed<TabletLifecycleEvent | null>(() => {
  const last = lastLifecycleEvent.value
  if (!last) return null

  const lastHourKey = dayjs(last.timestamp).format('YYYY-MM-DD-HH')
  const flappingThisHour = lifecycleEvents.value
    .filter((event) => event.eventType === 'flapping' && dayjs(event.timestamp).format('YYYY-MM-DD-HH') === lastHourKey)
    .sort((a, b) => dayjs(b.timestamp).diff(dayjs(a.timestamp)))[0]

  return flappingThisHour ?? last
})

const dotClass = (step: FlowStep, isAnomalous: boolean): string => {
  if (step.isPending) return 'animate-pulse border-2 border-amber-400 bg-amber-100'
  if (isAnomalous) return 'bg-red-500'
  return 'bg-primary'
}

const severityBadgeClass: Record<DisconnectSeverity, string> = {
  benign: 'bg-muted text-muted-foreground',
  watch: 'bg-amber-50 text-amber-600',
  concerning: 'bg-red-50 text-red-600'
}
const severityTextClass: Record<DisconnectSeverity, string> = {
  benign: 'text-muted-foreground',
  watch: 'text-amber-600',
  concerning: 'text-red-600'
}
const severityDotClass: Record<DisconnectSeverity, string> = {
  benign: 'bg-muted-foreground',
  watch: 'bg-amber-500',
  concerning: 'bg-red-500'
}

const lifecycleTitle = (event: TabletLifecycleEvent): string => {
  if (event.eventType === 'connected') return t('pages.marketplace.tabletsPage.orderFlow.legend.connected')
  if (event.eventType === 'flapping') return t('pages.marketplace.tabletsPage.orderFlow.legend.flapping')
  return t('pages.marketplace.tabletsPage.orderFlow.legend.disconnected')
}

const flappingRowLabel = (): string => t('pages.marketplace.tabletsPage.orderFlow.flapping.rowLabel')

const formatHourBucket = (timestamp: string): string => dayjs(timestamp).format('HH:00')

const flappingHourRange = (timestamp: string): string => {
  const start = dayjs(timestamp).startOf('hour')
  return `${start.format('HH:00')} → ${start.add(1, 'hour').format('HH:00')}`
}

// flapCount is only the size of the first 30s burst that crossed the threshold that hour — the
// backend stops writing anything else for the rest of the hour (backend#9444), so this is a
// read on that one burst, not a true rate for the whole hour — the detail box says so plainly.
const flappingBandSummary = (event: TabletLifecycleEvent): string =>
  t('pages.marketplace.tabletsPage.orderFlow.flapping.bandSummary', { count: event.flapCount ?? 0 })

const flappingRhythm = (event: TabletLifecycleEvent): string =>
  t('pages.marketplace.tabletsPage.orderFlow.flapping.rhythm', { count: event.flapCount ?? 0 })

const flappingLastStateLabel = (event: TabletLifecycleEvent): string | null => {
  if (event.lastState === 'connected') return t('pages.marketplace.tabletsPage.orderFlow.legend.connected')
  if (event.lastState === 'disconnected') return t('pages.marketplace.tabletsPage.orderFlow.legend.disconnected')
  return null
}

const flappingLastStateDotClass = (event: TabletLifecycleEvent): string =>
  event.lastState === 'connected' ? 'bg-green-500' : 'bg-red-500'

// Plain-language triage for AWS IoT's disconnectReason codes (life-cycle-events.html) — the
// severity drives the color, the label/explanation are the human-readable read, and the raw
// code stays available (monospace, in the expanded detail) for anyone filing a ticket.
const disconnectReasonLabel = (reason: string | null): string =>
  t(`pages.marketplace.tabletsPage.orderFlow.disconnectReasons.${getDisconnectReasonKey(reason)}.label`)

const disconnectReasonExplanation = (reason: string | null): string =>
  t(`pages.marketplace.tabletsPage.orderFlow.disconnectReasons.${getDisconnectReasonKey(reason)}.explanation`)

const severityLabel = (severity: DisconnectSeverity): string =>
  t(`pages.marketplace.tabletsPage.orderFlow.severity.${severity}`)

const deviceTitle = (event: TabletDeviceEvent): string => {
  const summary = resolveDeviceLinkSummary(event)
  if (summary) {
    return t(`pages.marketplace.tabletsPage.orderFlow.deviceEvents.summary.${summary.key}`, summary.params)
  }

  return t(`pages.marketplace.tabletsPage.orderFlow.deviceEvents.${event.eventType}`)
}

// "source" is the mechanism that changed the link (tablet self-claim, manual dashboard/support
// action, automatic reconciliation): reconciliation has no actor behind it, so it's labeled
// as automatic correction rather than fabricating a human origin.
const deviceSourceLabel = (event: TabletDeviceEvent): string =>
  t(`pages.marketplace.tabletsPage.orderFlow.deviceEvents.sourceLabel.${event.source}`)

// Names the specific segment that breached the anomaly threshold with a diagnosis, not just a
// generic "delay": each segment kind points to a different likely cause (slow partner POS,
// backend queue backlog, unreachable tablet), so support staff know where to look first.
function toAnomalyWarning(segments: FlowSegment[]): string | null {
  const segment = segments.find((s) => s.isAnomalous)
  if (!segment || !segment.formatted) return null

  return t(`pages.marketplace.tabletsPage.orderFlow.anomalyWarnings.${segment.kind}`, { duration: segment.formatted })
}

const stepTooltip = (kind: FlowStepKind): string =>
  t(`pages.marketplace.tabletsPage.orderFlow.stepExplanations.${kind}`)

const lifecycleExplanation = (): string => t('pages.marketplace.tabletsPage.orderFlow.explanations.lifecycle')

const deviceExplanation = (event: TabletDeviceEvent): string =>
  t(`pages.marketplace.tabletsPage.orderFlow.explanations.device.${event.source}`)

// "What changed" detail — null (nothing rendered) when there's no usable metadata.
const deviceMetadataLabel = (event: TabletDeviceEvent): string | null => {
  if (event.eventType.startsWith('tablet_offline_')) {
    const offline = readTabletOfflineMetadata(event.metadata)
    if (!offline) return null
    return t('pages.marketplace.tabletsPage.orderFlow.deviceEvents.offlineDetail', {
      duration: formatOfflineDuration(offline.offlineDurationMs),
      attempts: offline.reconnectAttempts
    })
  }

  const summary = summarizeTabletEventMetadata(event.metadata)
  if (!summary) return null

  if (summary.kind === 'fieldsOnly') {
    return summary.fields
      .map((field) =>
        field === 'application_version'
          ? t('pages.marketplace.tabletsPage.orderFlow.deviceEvents.applicationVersionChanged')
          : t('pages.marketplace.tabletsPage.orderFlow.deviceEvents.fieldChanged', { field })
      )
      .join(', ')
  }

  const booleanLabels = {
    on: t('pages.marketplace.tabletsPage.orderFlow.deviceEvents.booleanOn'),
    off: t('pages.marketplace.tabletsPage.orderFlow.deviceEvents.booleanOff')
  }
  const emptyLabel = t('pages.marketplace.tabletsPage.orderFlow.deviceEvents.meta.none')

  return summary.changes
    .map(({ field, from, to }) =>
      field === 'application_version'
        ? t('pages.marketplace.tabletsPage.orderFlow.deviceEvents.applicationVersionChangedTo', {
            from: formatTabletEventChangeValue(from, booleanLabels, emptyLabel),
            to: formatTabletEventChangeValue(to, booleanLabels, emptyLabel)
          })
        : t('pages.marketplace.tabletsPage.orderFlow.deviceEvents.changeLine', {
            field,
            from: formatTabletEventChangeValue(from, booleanLabels, emptyLabel),
            to: formatTabletEventChangeValue(to, booleanLabels, emptyLabel)
          })
    )
    .join(', ')
}

const orderValueClass = (row: FlowRow): string => {
  if (row.isPending) return 'font-semibold text-amber-600'
  if (row.isLatencyAnomalous)
    return 'inline-flex items-center rounded-md bg-red-50 px-2 py-0.5 font-semibold text-red-700'
  return 'text-muted-foreground'
}

const openConnectionFilter = (): void => {
  activeFilter.value = 'conn'
}

const orderValueText = (row: FlowRow): string => {
  if (row.isPending) return t('pages.marketplace.tabletsPage.orderFlow.pending')
  return row.totalMs !== null ? formatLatency(row.totalMs) : '—'
}

const currentStateSubtitle = computed<string | null>(() => {
  const event = effectiveCurrentEvent.value
  if (!event) return null

  const relative = dayRelativeLabel(dayjs(event.timestamp).format('YYYY-MM-DD'))
  const since =
    relative === 'today'
      ? t('pages.marketplace.tabletsPage.orderFlow.sinceToday')
      : relative === 'yesterday'
        ? t('pages.marketplace.tabletsPage.orderFlow.sinceYesterday')
        : t('pages.marketplace.tabletsPage.orderFlow.sinceDate', { date: dayjs(event.timestamp).format('L') })
  const time = formatTime(event.timestamp)
  const detail =
    event.eventType === 'flapping'
      ? flappingBandSummary(event)
      : event.disconnectReason
        ? disconnectReasonLabel(event.disconnectReason)
        : null

  return detail ? `${since} ${time} · ${detail}` : `${since} ${time}`
})

const refreshedLabel = computed(() => {
  void tick.value
  const diffSec = Math.max(0, Math.floor((Date.now() - lastRefreshedAt.value) / 1000))
  if (diffSec < 60) {
    return t('pages.marketplace.tabletsPage.orderFlow.refreshedAt', {
      time: dayjs(lastRefreshedAt.value).format('HH:mm')
    })
  }
  return t('pages.marketplace.tabletsPage.orderFlow.refreshedMinutesAgo', { minutes: Math.floor(diffSec / 60) })
})

const close = () => emit('update:isOpen', false)

// Range changes, live polling and search can all call fetchFlow() while a previous call is
// still in flight: only the response matching the latest requestId is applied, so a slower
// stale request can't overwrite the screen after a newer one already landed.
let latestRequestId = 0

const fetchFlow = async () => {
  const requestId = ++latestRequestId
  isLoading.value = true

  try {
    // Tolerate a leading '#' — support staff often paste the order number as shown on
    // the tablet/receipt, which includes it.
    const cleanedSearch = searchQuery.value.trim().replace(/^#/, '')
    const newFlow = await getTabletOrdersFlow(
      props.tablet.id,
      cleanedSearch || undefined,
      hasSearched.value ? undefined : apiEventRange.value
    )

    // A search narrows to a specific (maybe old) order: anchor both event types to it so
    // whatever happened right before/after it shows up, even outside their default windows.
    const around = hasSearched.value && newFlow.length > 0 ? toFlowRow(newFlow[0]).rawStart : undefined
    const [newLifecycleEvents, newDeviceEvents] = await Promise.all([
      getTabletLifecycleEvents(props.tablet.id, around, apiEventRange.value),
      getTabletDeviceEvents(props.tablet.id, around, apiEventRange.value)
    ])

    if (requestId !== latestRequestId) return

    flow.value = newFlow
    lifecycleEvents.value = newLifecycleEvents
    deviceEvents.value = newDeviceEvents
    // Everything starts collapsed: the timeline is scanned via the one-line rows first,
    // detail is opt-in.
    openState.value = {}
    lastRefreshedAt.value = Date.now()
    nextTick(syncDayGroupCollapse)
  } catch (error: unknown) {
    if (requestId !== latestRequestId) return

    notification.create('error', {
      text: getApiErrorMessage(error) ?? t('pages.marketplace.tabletsPage.orderFlow.fetchError')
    })
  } finally {
    if (requestId === latestRequestId) isLoading.value = false
  }
}

const debouncedFetchFlow = useDebounceFn(fetchFlow, 300)
const handleSearch = () => debouncedFetchFlow()

const stopPolling = () => {
  if (pollTimer) clearInterval(pollTimer)
  if (tickTimer) clearInterval(tickTimer)
  pollTimer = null
  tickTimer = null
}

const startPolling = () => {
  stopPolling()
  pollTimer = setInterval(() => {
    if (isLive.value) fetchFlow()
  }, POLL_INTERVAL_MS)
  tickTimer = setInterval(() => {
    tick.value++
  }, 1000)
}

const toggleLive = () => {
  isLive.value = !isLive.value
}

const handleRangeChange = (value: string) => {
  eventRange.value = value as RangeSelection
  fetchFlow()
}

watch(activeFilter, () => {
  nextTick(syncDayGroupCollapse)
})

watch(
  () => props.isOpen,
  (open) => {
    if (!open) {
      stopPolling()
      return
    }

    searchQuery.value = ''
    activeFilter.value = 'all'
    eventRange.value = 'default'
    isLive.value = false // Paused by default: an agent opens this to inspect a snapshot, not to babysit a live feed.
    collapsedDayKeys.value = new Set()
    fetchFlow()
    startPolling()
  }
)

onUnmounted(stopPolling)
</script>

<template>
  <BaseModal
    :is-open="isOpen"
    :heading-title="t('pages.marketplace.tabletsPage.orderFlow.title', { id: tablet.id })"
    size="5xl"
    :show-footer="true"
    @close="close"
  >
    <template #customHeadingTitle>
      <div class="flex w-full items-start justify-between gap-3 pr-6">
        <div class="min-w-0">
          <div class="flex flex-wrap items-center gap-3">
            <h3 class="text-lg font-semibold leading-none text-foreground">
              {{ t('pages.marketplace.tabletsPage.orderFlow.title', { id: tablet.id }) }}
            </h3>
            <BaseSupportConnection />
            <AppTooltip :text="t('pages.marketplace.tabletsPage.orderFlow.betaTooltip')" tooltip-position="bottom">
              <BaseChip :text="t('pages.marketplace.tabletsPage.orderFlow.beta')" />
            </AppTooltip>
          </div>
          <div class="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            <span>{{ tablet.storeName }}</span>
            <template v-if="tablet.applicationVersion">
              <span>·</span>
              <span>{{ tablet.applicationVersion }} · {{ tablet.platformShort }}</span>
            </template>
            <span>·</span>
            <span class="font-mono">tab-{{ tablet.id }}</span>
          </div>
        </div>
        <AppTooltip :text="refreshedLabel" tooltip-position="bottom">
          <Button
            variant="ghost"
            size="sm"
            class="shrink-0 gap-1.5 text-xs font-semibold"
            :class="isLive ? 'bg-primary/10 text-primary' : 'text-muted-foreground'"
            @click="toggleLive"
          >
            <span
              class="block h-1.5 w-1.5 rounded-full"
              :class="isLive ? 'animate-pulse bg-primary' : 'bg-muted-foreground'"
            />
            {{
              isLive
                ? t('pages.marketplace.tabletsPage.orderFlow.live')
                : t('pages.marketplace.tabletsPage.orderFlow.paused')
            }}
          </Button>
        </AppTooltip>
      </div>

      <div
        class="mt-4 grid grid-cols-2 gap-px overflow-hidden rounded-lg border bg-muted sm:grid-cols-3 lg:grid-cols-5"
      >
        <div class="bg-white p-3">
          <div class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {{ t('pages.marketplace.tabletsPage.orderFlow.stats.currentState') }}
          </div>
          <div class="mt-1.5 flex items-center gap-1.5">
            <span
              v-if="effectiveCurrentEvent"
              class="block h-2 w-2 shrink-0 rounded-full"
              :class="
                effectiveCurrentEvent.eventType === 'connected'
                  ? 'bg-green-500'
                  : effectiveCurrentEvent.eventType === 'flapping'
                    ? severityDotClass.concerning
                    : severityDotClass[getDisconnectSeverity(effectiveCurrentEvent.disconnectReason ?? null)]
              "
            />
            <span
              class="text-sm font-semibold"
              :class="effectiveCurrentEvent ? 'text-foreground' : 'font-normal text-muted-foreground'"
            >
              {{
                effectiveCurrentEvent
                  ? lifecycleTitle(effectiveCurrentEvent)
                  : t('pages.marketplace.tabletsPage.orderFlow.stats.noData')
              }}
            </span>
          </div>
          <div v-if="currentStateSubtitle" class="mt-0.5 text-xs leading-snug text-muted-foreground">
            {{ currentStateSubtitle }}
          </div>
        </div>

        <div class="bg-white p-3">
          <div class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {{ t('pages.marketplace.tabletsPage.orderFlow.stats.medianLatency') }}
          </div>
          <div class="mt-1.5 text-sm font-semibold text-foreground">
            {{ medianLatencyMs !== null ? formatLatency(medianLatencyMs) : '—' }}
          </div>
          <div class="mt-0.5 text-xs leading-snug text-muted-foreground">
            {{ t('pages.marketplace.tabletsPage.orderFlow.stats.latencyThresholdInfo', { count: rows.length }) }}
          </div>
        </div>

        <div class="bg-white p-3">
          <div class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {{ t('pages.marketplace.tabletsPage.orderFlow.stats.latencyAnomalies') }}
          </div>
          <div
            class="mt-1.5 text-sm font-semibold"
            :class="latencyAnomalyCount > 0 ? 'text-red-600' : 'text-green-600'"
          >
            {{
              latencyAnomalyCount > 0
                ? t('pages.marketplace.tabletsPage.orderFlow.stats.anomalyCount', { count: latencyAnomalyCount })
                : t('pages.marketplace.tabletsPage.orderFlow.stats.noAnomalies')
            }}
          </div>
          <div class="mt-0.5 text-xs leading-snug text-muted-foreground">
            {{ t('pages.marketplace.tabletsPage.orderFlow.stats.latencyAnomalyInfo', { count: rows.length }) }}
          </div>
        </div>

        <div class="bg-white p-3">
          <div class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {{ t('pages.marketplace.tabletsPage.orderFlow.stats.unacked') }}
          </div>
          <div class="mt-1.5 text-sm font-semibold" :class="unackedCount > 0 ? 'text-amber-600' : 'text-green-600'">
            {{
              unackedCount > 0
                ? t('pages.marketplace.tabletsPage.orderFlow.stats.unackedCount', { count: unackedCount })
                : t('pages.marketplace.tabletsPage.orderFlow.stats.allAcked')
            }}
          </div>
          <div class="mt-0.5 text-xs leading-snug text-muted-foreground">
            {{ t('pages.marketplace.tabletsPage.orderFlow.stats.unackedInfo', { count: rows.length }) }}
          </div>
        </div>

        <div class="bg-white p-3">
          <div class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {{ t('pages.marketplace.tabletsPage.orderFlow.stats.disconnections') }}
          </div>
          <div class="mt-1.5 flex items-center gap-1.5">
            <span v-if="flappingHoursCount > 0" class="block h-2 w-2 shrink-0 rounded-full bg-red-500" />
            <span class="text-sm font-semibold" :class="flappingHoursCount > 0 ? 'text-red-600' : 'text-foreground'">
              {{ significantDisconnectionCount }}
            </span>
          </div>
          <div v-if="lastDisconnectEvent" class="mt-0.5 text-xs leading-snug text-muted-foreground">
            {{
              t('pages.marketplace.tabletsPage.orderFlow.stats.lastDisconnectionAt', {
                time: formatTime(lastDisconnectEvent.timestamp)
              })
            }}
          </div>
          <div v-if="flappingHoursCount > 0" class="mt-0.5 text-xs leading-snug text-red-500">
            {{ t('pages.marketplace.tabletsPage.orderFlow.stats.flappingHours', { count: flappingHoursCount }) }}
          </div>
        </div>
      </div>

      <div
        v-if="showUnackedHealthBanner"
        class="mt-3 flex flex-col gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 sm:flex-row sm:items-center sm:justify-between"
      >
        <p class="text-xs leading-relaxed text-amber-950">
          {{ t('pages.marketplace.tabletsPage.orderFlow.healthBanner.unacked', { count: unackedCount }) }}
        </p>
        <Button
          variant="outline"
          size="sm"
          class="shrink-0 border-amber-300 bg-white font-semibold text-amber-900 hover:bg-amber-100"
          @click="openConnectionFilter"
        >
          {{ t('pages.marketplace.tabletsPage.orderFlow.healthBanner.viewConnection') }}
        </Button>
      </div>

      <div class="mt-3 flex flex-wrap items-center gap-2">
        <div class="relative min-w-[220px] flex-1">
          <Search class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            v-model="searchQuery"
            type="search"
            class="pl-10"
            :placeholder="t('pages.marketplace.tabletsPage.orderFlow.searchPlaceholder')"
            @input="handleSearch"
          />
        </div>
        <Button
          v-for="chip in filterChips"
          :key="chip.key"
          class="gap-1.5 font-semibold"
          :variant="activeFilter === chip.key ? 'default-primary' : 'outline'"
          @click="activeFilter = chip.key"
        >
          {{ chip.label }}
          <span :class="activeFilter === chip.key ? 'text-primary-foreground/70' : 'text-muted-foreground'">{{
            chip.count
          }}</span>
        </Button>
        <Select :model-value="eventRange" @update:model-value="handleRangeChange">
          <SelectTrigger class="h-10 font-semibold text-foreground-secondary">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem v-for="option in rangeOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <p v-if="!isLoading && filteredTimelineItems.length > 0" class="mt-2 text-xs text-muted-foreground">
        {{
          t('pages.marketplace.tabletsPage.orderFlow.timelineSummary', {
            range: activeRangeLabel,
            count: filteredTimelineItems.length,
            days: groupedByDay.length
          })
        }}
        <button
          v-if="collapsedDayCount > 0"
          type="button"
          class="ml-1 font-semibold text-primary underline-offset-2 hover:underline"
          @click="expandAllDayGroups"
        >
          {{ t('pages.marketplace.tabletsPage.orderFlow.expandAllDays', { count: collapsedDayCount }) }}
        </button>
      </p>
    </template>

    <AppSkeleton v-if="isLoading" type="list" />
    <div v-else-if="groupedByDay.length === 0" class="py-12 text-center text-sm text-muted-foreground">
      {{
        hasSearched
          ? t('pages.marketplace.tabletsPage.orderFlow.noResults', { searchTerm: searchQuery })
          : t('pages.marketplace.tabletsPage.orderFlow.empty')
      }}
    </div>
    <div v-else class="max-h-[min(62vh,720px)] overflow-y-auto pr-1">
      <div v-for="group in groupedByDay" :key="group.key">
        <button
          type="button"
          class="sticky top-0 z-10 flex w-full items-baseline gap-2 bg-white/95 px-1 py-2 text-left backdrop-blur-sm hover:bg-muted/40"
          @click="toggleDayGroup(group.key)"
        >
          <ChevronRight
            class="size-4 shrink-0 text-muted-foreground transition-transform duration-200"
            :class="isDayGroupExpanded(group.key) && 'rotate-90'"
          />
          <span class="text-xs font-bold uppercase text-foreground">{{ group.subtitle ?? group.title }}</span>
          <span v-if="group.subtitle" class="text-xs text-muted-foreground">{{ group.title }}</span>
          <span class="ml-auto text-xs text-muted-foreground">
            {{ t('pages.marketplace.tabletsPage.orderFlow.eventsCount', { count: group.items.length }) }}
          </span>
        </button>

        <div v-show="isDayGroupExpanded(group.key)">
          <div v-for="item in group.items" :key="item.key" class="mb-2">
            <!-- Order -->
            <Collapsible v-if="item.kind === 'order'" v-model:open="openState[item.key]">
              <CollapsibleTrigger
                class="flex h-12 w-full items-center gap-3 rounded-lg border bg-white px-3 text-left transition-colors hover:bg-muted"
              >
                <ChevronRight
                  class="size-4 shrink-0 text-muted-foreground transition-transform duration-200"
                  :class="openState[item.key] && 'rotate-90'"
                />
                <span
                  class="flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground"
                >
                  <ArrowRight class="size-3" />
                </span>
                <span class="w-[68px] shrink-0 text-xs tabular-nums text-muted-foreground">{{
                  formatTime(item.row.rawStart)
                }}</span>
                <span class="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
                  {{ t('pages.marketplace.tabletsPage.orderFlow.orderLabel') }} #{{
                    item.row.orderNumber ?? item.row.orderId
                  }}
                </span>
                <span class="shrink-0 text-xs" :class="orderValueClass(item.row)">{{ orderValueText(item.row) }}</span>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div class="px-1 pb-1 pt-2">
                  <div class="rounded-lg bg-muted px-3.5 py-3 ring-1 ring-inset ring-border">
                    <div class="mb-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {{ t('pages.marketplace.tabletsPage.orderFlow.stepsTitle') }}
                    </div>
                    <div class="flex items-start pb-1">
                      <template v-for="(step, i) in item.row.steps" :key="step.label">
                        <div class="flex min-w-0 shrink-0 basis-14 flex-col items-center text-center">
                          <span
                            class="block h-2.5 w-2.5 rounded-full"
                            :class="dotClass(step, item.row.segments[i]?.isAnomalous ?? false)"
                          />
                          <AppTooltip :text="stepTooltip(step.kind)" tooltip-position="top">
                            <span class="mt-2 text-xs font-medium text-muted-foreground">{{ step.label }}</span>
                          </AppTooltip>
                          <span class="text-xs tabular-nums text-muted-foreground">{{ step.time ?? '—' }}</span>
                          <span v-if="step.meta" class="text-xs text-muted-foreground">{{ step.meta }}</span>
                        </div>
                        <div v-if="i < item.row.steps.length - 1" class="relative mt-[4px] flex-1">
                          <div
                            class="h-px w-full"
                            :class="[
                              item.row.segments[i].formatted === null && 'border-t border-dashed border-amber-300',
                              item.row.segments[i].formatted !== null &&
                                item.row.segments[i].isAnomalous &&
                                'bg-red-300',
                              item.row.segments[i].formatted !== null &&
                                !item.row.segments[i].isAnomalous &&
                                'bg-primary/20'
                            ]"
                          />
                          <span
                            class="absolute inset-x-0 -top-5 mx-auto w-fit text-center text-xs tabular-nums"
                            :class="
                              item.row.segments[i].isAnomalous
                                ? 'rounded bg-red-50 px-1.5 py-0.5 font-semibold text-red-700'
                                : 'text-muted-foreground'
                            "
                          >
                            {{ item.row.segments[i].formatted ?? t('pages.marketplace.tabletsPage.orderFlow.pending') }}
                          </span>
                        </div>
                      </template>
                    </div>

                    <div
                      v-if="item.row.unackedWarning"
                      class="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 p-2.5 text-xs leading-relaxed text-amber-950"
                    >
                      <span class="mt-0.5 block size-1.5 shrink-0 rounded-full bg-amber-500" />
                      <span>{{ item.row.unackedWarning }}</span>
                    </div>
                    <div
                      v-if="item.row.warning"
                      class="mt-3 flex items-start gap-2 rounded-lg bg-red-50 p-2.5 text-xs leading-relaxed text-red-800"
                    >
                      <span class="mt-0.5 block size-1.5 shrink-0 rounded-full bg-red-500" />
                      <span>{{ item.row.warning }}</span>
                    </div>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>

            <!-- Lifecycle, connected, single occurrence: no detail worth expanding -->
            <div
              v-else-if="
                item.kind === 'lifecycle' && item.events[0].eventType === 'connected' && item.events.length === 1
              "
              class="flex h-12 items-center gap-3 rounded-lg border bg-white px-3"
            >
              <span class="size-4 shrink-0" />
              <span class="flex size-5 shrink-0 items-center justify-center rounded-full bg-green-50 text-green-600">
                <Check class="size-3" />
              </span>
              <span class="w-[68px] shrink-0 text-xs tabular-nums text-muted-foreground">{{
                formatTime(item.events[0].timestamp)
              }}</span>
              <AppTooltip :text="lifecycleExplanation()" tooltip-position="right">
                <span class="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
                  {{ lifecycleTitle(item.events[0]) }}
                </span>
              </AppTooltip>
            </div>

            <!-- Lifecycle, connected, merged duplicates -->
            <Collapsible
              v-else-if="item.kind === 'lifecycle' && item.events[0].eventType === 'connected'"
              v-model:open="openState[item.key]"
            >
              <CollapsibleTrigger
                class="flex h-12 w-full items-center gap-3 rounded-lg border bg-white px-3 text-left transition-colors hover:bg-muted"
              >
                <ChevronRight
                  class="size-4 shrink-0 text-muted-foreground transition-transform duration-200"
                  :class="openState[item.key] && 'rotate-90'"
                />
                <span class="flex size-5 shrink-0 items-center justify-center rounded-full bg-green-50 text-green-600">
                  <Check class="size-3" />
                </span>
                <span class="w-[68px] shrink-0 text-xs tabular-nums text-muted-foreground">{{
                  formatTime(item.events[0].timestamp)
                }}</span>
                <span class="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
                  {{ lifecycleTitle(item.events[0]) }}
                  <BaseChip class="ml-1.5" :text="`×${item.events.length}`" />
                </span>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div class="space-y-1 py-2 pl-9 pr-2 text-xs text-muted-foreground">
                  <div class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {{ t('pages.marketplace.tabletsPage.orderFlow.occurrences') }}
                  </div>
                  <div v-for="(occurrence, idx) in item.events" :key="idx">{{ formatTime(occurrence.timestamp) }}</div>
                </div>
              </CollapsibleContent>
            </Collapsible>

            <!-- Lifecycle, flapping: throttled hourly summary of a connect/disconnect burst (backend#9444) -->
            <Collapsible
              v-else-if="item.kind === 'lifecycle' && item.events[0].eventType === 'flapping'"
              v-model:open="openState[item.key]"
            >
              <CollapsibleTrigger
                class="flex h-12 w-full items-center gap-3 rounded-lg border bg-white px-3 text-left transition-colors hover:bg-muted"
              >
                <ChevronRight
                  class="size-4 shrink-0 text-muted-foreground transition-transform duration-200"
                  :class="openState[item.key] && 'rotate-90'"
                />
                <span class="flex size-5 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600">
                  <Activity class="size-3" />
                </span>
                <span class="w-[68px] shrink-0 text-xs tabular-nums text-muted-foreground">{{
                  formatHourBucket(item.events[0].timestamp)
                }}</span>
                <span class="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
                  {{ flappingRowLabel() }}
                </span>
                <BaseChip
                  class="shrink-0"
                  color="red"
                  :text="t('pages.marketplace.tabletsPage.orderFlow.legend.flapping')"
                >
                  <template #leading>
                    <Activity />
                  </template>
                </BaseChip>
              </CollapsibleTrigger>

              <div class="mx-1 mb-0 flex items-center justify-between gap-3 rounded-md bg-red-50 px-3 py-1.5">
                <div class="flex min-w-0 items-center gap-2">
                  <span class="shrink-0 text-xs tabular-nums text-muted-foreground">{{
                    flappingHourRange(item.events[0].timestamp)
                  }}</span>
                  <span class="flex shrink-0 items-center gap-1" aria-hidden="true">
                    <span
                      v-for="bar in Math.min(item.events[0].flapCount ?? 0, 16)"
                      :key="bar"
                      class="inline-block h-3.5 w-1 rounded-full bg-red-600"
                    />
                  </span>
                  <span class="truncate text-xs font-medium text-red-700">{{
                    flappingBandSummary(item.events[0])
                  }}</span>
                </div>
                <div v-if="flappingLastStateLabel(item.events[0])" class="flex shrink-0 items-center gap-1.5 text-xs">
                  <span class="text-muted-foreground">{{
                    t('pages.marketplace.tabletsPage.orderFlow.flapping.lastStateLabel')
                  }}</span>
                  <span class="block h-1.5 w-1.5 rounded-full" :class="flappingLastStateDotClass(item.events[0])" />
                  <span class="font-medium text-foreground">{{ flappingLastStateLabel(item.events[0]) }}</span>
                </div>
              </div>

              <CollapsibleContent>
                <div class="py-2 pl-9 pr-2">
                  <div class="rounded-lg bg-muted px-3.5 py-3 ring-1 ring-inset ring-border">
                    <div class="flex flex-wrap gap-x-6 gap-y-3">
                      <div class="shrink-0">
                        <div class="text-xs text-muted-foreground">
                          {{ t('pages.marketplace.tabletsPage.orderFlow.flapping.detail.hourCovered') }}
                        </div>
                        <div class="mt-0.5 text-xs font-semibold text-foreground">
                          {{ flappingHourRange(item.events[0].timestamp) }}
                        </div>
                      </div>
                      <div class="shrink-0">
                        <div class="text-xs text-muted-foreground">
                          {{ t('pages.marketplace.tabletsPage.orderFlow.flapping.detail.recordedAt') }}
                        </div>
                        <div class="mt-0.5 text-xs font-semibold tabular-nums text-foreground">
                          {{ formatTime(item.events[0].timestamp) }}
                        </div>
                      </div>
                      <div v-if="flappingLastStateLabel(item.events[0])" class="shrink-0">
                        <div class="text-xs text-muted-foreground">
                          {{ t('pages.marketplace.tabletsPage.orderFlow.flapping.detail.lastKnownState') }}
                        </div>
                        <div class="mt-0.5 text-xs font-semibold text-foreground">
                          {{ flappingLastStateLabel(item.events[0]) }}
                        </div>
                      </div>
                      <div v-if="item.events[0].disconnectReason" class="shrink-0">
                        <div class="text-xs text-muted-foreground">
                          {{ t('pages.marketplace.tabletsPage.orderFlow.flapping.detail.lastKnownReason') }}
                        </div>
                        <div class="mt-0.5 text-xs font-semibold text-foreground">
                          {{ disconnectReasonLabel(item.events[0].disconnectReason) }}
                        </div>
                      </div>
                      <div class="shrink-0">
                        <div class="text-xs text-muted-foreground">
                          {{ t('pages.marketplace.tabletsPage.orderFlow.flapping.detail.rhythm') }}
                        </div>
                        <div class="mt-0.5 text-xs font-semibold text-red-600">
                          {{ flappingRhythm(item.events[0]) }}
                        </div>
                      </div>
                    </div>

                    <div class="mt-3">
                      <div class="text-xs text-muted-foreground">
                        {{ t('pages.marketplace.tabletsPage.orderFlow.disconnectDetail.whatItMeans') }}
                      </div>
                      <div class="mt-0.5 text-xs leading-relaxed text-foreground">
                        {{ t('pages.marketplace.tabletsPage.orderFlow.flapping.guidance') }}
                      </div>
                    </div>

                    <div class="mt-3 flex flex-wrap items-start gap-6">
                      <div class="min-w-[220px] flex-1">
                        <div class="text-xs text-muted-foreground">
                          {{ t('pages.marketplace.tabletsPage.orderFlow.flapping.detail.scope') }}
                        </div>
                        <div class="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                          {{ t('pages.marketplace.tabletsPage.orderFlow.flapping.scopeDisclaimer') }}
                        </div>
                      </div>
                      <div class="shrink-0">
                        <div class="text-xs text-muted-foreground">
                          {{ t('pages.marketplace.tabletsPage.orderFlow.disconnectDetail.technicalCode') }}
                        </div>
                        <div class="mt-0.5 font-mono text-xs text-muted-foreground">
                          {{ getDisconnectReasonKey(item.events[0].disconnectReason) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>

            <!-- Lifecycle, disconnected: always expandable — severity, explanation and raw code live in the detail -->
            <Collapsible v-else-if="item.kind === 'lifecycle'" v-model:open="openState[item.key]">
              <CollapsibleTrigger
                class="flex h-12 w-full items-center gap-3 rounded-lg border bg-white px-3 text-left transition-colors hover:bg-muted"
              >
                <ChevronRight
                  class="size-4 shrink-0 text-muted-foreground transition-transform duration-200"
                  :class="openState[item.key] && 'rotate-90'"
                />
                <span
                  class="flex size-5 shrink-0 items-center justify-center rounded-full"
                  :class="severityBadgeClass[getDisconnectSeverity(item.events[0].disconnectReason)]"
                >
                  <X class="size-3" />
                </span>
                <span class="w-[68px] shrink-0 text-xs tabular-nums text-muted-foreground">{{
                  formatTime(item.events[0].timestamp)
                }}</span>
                <span class="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
                  {{ lifecycleTitle(item.events[0]) }}
                  <span
                    v-if="item.events.length > 1"
                    class="ml-1.5 rounded bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground"
                  >
                    ×{{ item.events.length }}
                  </span>
                </span>
                <span class="shrink-0 text-xs text-muted-foreground">{{
                  disconnectReasonLabel(item.events[0].disconnectReason)
                }}</span>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div class="py-2 pl-9 pr-2">
                  <div
                    class="flex flex-wrap items-start gap-6 rounded-lg bg-muted px-3.5 py-3 ring-1 ring-inset ring-border"
                  >
                    <div class="shrink-0">
                      <div class="text-xs text-muted-foreground">
                        {{ t('pages.marketplace.tabletsPage.orderFlow.disconnectDetail.severity') }}
                      </div>
                      <div
                        class="mt-0.5 text-xs font-semibold"
                        :class="severityTextClass[getDisconnectSeverity(item.events[0].disconnectReason)]"
                      >
                        {{ severityLabel(getDisconnectSeverity(item.events[0].disconnectReason)) }}
                      </div>
                    </div>
                    <div class="min-w-[220px] flex-1">
                      <div class="text-xs text-muted-foreground">
                        {{ t('pages.marketplace.tabletsPage.orderFlow.disconnectDetail.whatItMeans') }}
                      </div>
                      <div class="mt-0.5 text-xs leading-relaxed text-foreground">
                        {{ disconnectReasonExplanation(item.events[0].disconnectReason) }}
                      </div>
                    </div>
                    <div class="shrink-0">
                      <div class="text-xs text-muted-foreground">
                        {{ t('pages.marketplace.tabletsPage.orderFlow.disconnectDetail.technicalCode') }}
                      </div>
                      <div class="mt-0.5 font-mono text-xs text-muted-foreground">
                        {{ getDisconnectReasonKey(item.events[0].disconnectReason) }}
                      </div>
                    </div>
                  </div>

                  <div v-if="item.events.length > 1" class="mt-2.5 space-y-1">
                    <div class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {{ t('pages.marketplace.tabletsPage.orderFlow.occurrences') }}
                    </div>
                    <div v-for="(occurrence, idx) in item.events" :key="idx" class="text-xs text-muted-foreground">
                      {{ formatTime(occurrence.timestamp) }}
                    </div>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>

            <!-- Device / action events -->
            <Collapsible v-else-if="item.kind === 'device'" v-model:open="openState[item.key]">
              <CollapsibleTrigger
                class="flex h-12 w-full items-center gap-3 rounded-lg border bg-white px-3 text-left transition-colors hover:bg-muted"
              >
                <ChevronRight
                  class="size-4 shrink-0 text-muted-foreground transition-transform duration-200"
                  :class="openState[item.key] && 'rotate-90'"
                />
                <AppTooltip :text="deviceExplanation(item.events[0])" tooltip-position="right">
                  <span
                    class="flex size-5 shrink-0 items-center justify-center rounded-md bg-white text-foreground-secondary ring-1 ring-inset ring-border"
                  >
                    <Power class="size-3" />
                  </span>
                </AppTooltip>
                <span class="w-[68px] shrink-0 text-xs tabular-nums text-muted-foreground">{{
                  formatTime(item.events[0].timestamp)
                }}</span>
                <span class="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
                  {{ deviceTitle(item.events[0]) }}
                  <span
                    v-if="item.events.length > 1"
                    class="ml-1.5 rounded bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground"
                  >
                    ×{{ item.events.length }}
                  </span>
                </span>
                <span v-if="item.events[0].actorName" class="shrink-0 truncate text-xs text-muted-foreground">
                  {{ item.events[0].actorName }}
                </span>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div class="py-2 pl-9 pr-2">
                  <div
                    class="grid grid-cols-2 gap-x-6 gap-y-2 rounded-lg bg-muted px-3.5 py-2.5 ring-1 ring-inset ring-border sm:grid-cols-4"
                  >
                    <div>
                      <div class="text-xs text-muted-foreground">
                        {{ t('pages.marketplace.tabletsPage.orderFlow.deviceEvents.meta.origin') }}
                      </div>
                      <div
                        class="mt-0.5 text-xs font-semibold"
                        :class="
                          item.events[0].source === 'reconciliation'
                            ? 'italic text-muted-foreground'
                            : 'text-foreground'
                        "
                      >
                        {{ deviceSourceLabel(item.events[0]) }}
                      </div>
                    </div>
                    <div v-if="item.events[0].actorName">
                      <div class="text-xs text-muted-foreground">
                        {{ t('pages.marketplace.tabletsPage.orderFlow.deviceEvents.meta.actor') }}
                      </div>
                      <div class="mt-0.5 text-xs font-semibold text-foreground">{{ item.events[0].actorName }}</div>
                    </div>
                    <div v-if="item.events[0].previousDeviceId">
                      <div class="text-xs text-muted-foreground">
                        {{ t('pages.marketplace.tabletsPage.orderFlow.deviceEvents.meta.previousDevice') }}
                      </div>
                      <div class="mt-0.5 font-mono text-xs font-semibold text-foreground">
                        {{ item.events[0].previousDeviceId }}
                      </div>
                    </div>
                    <div v-if="item.events[0].deviceId">
                      <div class="text-xs text-muted-foreground">
                        {{ t('pages.marketplace.tabletsPage.orderFlow.deviceEvents.meta.nextDevice') }}
                      </div>
                      <div class="mt-0.5 font-mono text-xs font-semibold text-foreground">
                        {{ item.events[0].deviceId }}
                      </div>
                    </div>
                  </div>

                  <div v-if="deviceMetadataLabel(item.events[0])" class="mt-2 text-xs text-muted-foreground">
                    {{ deviceMetadataLabel(item.events[0]) }}
                  </div>

                  <div v-if="item.events[0].previousDeviceId" class="mt-2.5 flex items-center gap-2.5">
                    <span class="shrink-0 text-xs text-muted-foreground">
                      {{
                        t('pages.marketplace.tabletsPage.orderFlow.deviceEvents.breakNote', {
                          deviceId: item.events[0].previousDeviceId
                        })
                      }}
                    </span>
                    <span class="h-px flex-1 border-t border-dashed border" />
                  </div>

                  <div v-if="item.events.length > 1" class="mt-2.5 space-y-1">
                    <div class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {{ t('pages.marketplace.tabletsPage.orderFlow.occurrences') }}
                    </div>
                    <div v-for="(occurrence, idx) in item.events" :key="idx" class="text-xs text-muted-foreground">
                      {{ formatTime(occurrence.timestamp) }}
                    </div>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </div>
      </div>

      <div class="py-4 text-center text-xs text-muted-foreground">
        {{ t('pages.marketplace.tabletsPage.orderFlow.startOfPeriod') }}
      </div>
    </div>

    <template #linkBtn>
      <div class="flex w-full items-center justify-between">
        <span class="text-xs text-muted-foreground">
          {{ t('pages.marketplace.tabletsPage.orderFlow.footer.eventsShown', { count: filteredTimelineItems.length }) }}
          ·
          {{
            isLive
              ? t('pages.marketplace.tabletsPage.orderFlow.footer.live')
              : t('pages.marketplace.tabletsPage.orderFlow.footer.paused')
          }}
        </span>
        <Button variant="outline" @click="close">{{ t('global.words.close') }}</Button>
      </div>
    </template>
  </BaseModal>
</template>
